import { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Tooltip } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import GooglePlacesAutocompleteInput from "../../common/GooglePlacesAutocompleteInput";
import useAppConfig from "../../hooks/access-control/useAppConfig";
import useUsers from "../../hooks/userManagement/useUsers";
import { useFileUpload } from "../../hooks/useFileUpload";
import useWorkspaceMembers, {
  getWorkspaceMemberDisplayName,
  getWorkspaceMemberEmail,
  getWorkspaceMemberUser,
} from "../../hooks/workspaces/useWorkspaceMembers";
import { careService } from "../../services/careService";
import { olreadiaService } from "../../services/olreadiaService";
import ProfessionalProfileDetails from "../ProfessionalProfileDetails";
import UserProfilePage from "../../users/profile/UserProfilePage";
import { profileTitle, toList, titleCase } from "../professionalProfileUtils";
import { addressFieldsFromPlaceDetails } from "../../utils/googlePlaces";
import paths from "../../routes/paths";

const steps = [
  { key: "profile", label: "Profile Preview" },
  { key: "professional", label: "Profession Preview" },
  { key: "care", label: "Home Care Staff" },
  { key: "review", label: "Review" },
];
const sourceOnlyStepKeys = new Set(["profile", "professional"]);
const APPLICATION_REFRESH_DELAY_MS = 650;
const professionalProfilesPath = "/hire-care-giver/professional-profiles";

const refreshApplicationSoon = () => {
  if (typeof window === "undefined") return;
  window.setTimeout(() => window.location.reload(), APPLICATION_REFRESH_DELAY_MS);
};

const stepIndexForKey = (stepKey) => {
  const index = steps.findIndex((step) => step.key === stepKey);
  return index >= 0 ? index : 0;
};

const shiftOptions = ["Day", "Night", "Any", "Morning", "Afternoon", "Evening", "Overnight", "Live-in", "Weekend", "On call"];
const workDayOptions = ["Weekdays", "Weekends", "Holidays", "Day shifts", "Night shifts", "Any"];
const languageOptions = ["English", "Spanish", "French", "Portuguese", "Arabic", "Amharic", "Swahili", "Chinese", "Tagalog", "Hindi", "Yoruba", "Igbo", "Twi", "Other"];
const workAuthorizationTypes = [
  ["US_CITIZEN", "U.S. Citizen"],
  ["PERMANENT_RESIDENT", "Permanent Resident / Green Card"],
  ["EMPLOYMENT_AUTHORIZATION_DOCUMENT", "Employment Authorization Document (EAD)"],
  ["TEMPORARY_PROTECTED_STATUS", "Temporary Protected Status (TPS)"],
  ["ASYLUM", "Asylum"],
  ["REFUGEE", "Refugee"],
  ["DACA", "DACA"],
  ["INTERNATIONAL_STUDENT", "International Student"],
  ["WORK_VISA", "Work Visa"],
  ["DEPENDENT_VISA_WITH_WORK_AUTH", "Dependent Visa With Work Authorization"],
  ["NOT_AUTHORIZED", "Not Currently Authorized"],
  ["OTHER", "Other"],
];
const manualReviewWorkAuthorizationTypes = new Set([
  "INTERNATIONAL_STUDENT",
  "WORK_VISA",
  "DEPENDENT_VISA_WITH_WORK_AUTH",
]);
const addressFields = [
  ["addressLine1", "Street address"],
  ["city", "City"],
  ["county", "County"],
  ["state", "State"],
  ["country", "Country"],
  ["zipCode", "ZIP/postal code"],
];

const clean = (value) => String(value || "").trim();

const currentUserId = (user = {}) => user?.id || user?.userId || user?.uid || "";

const isPersonalWorkspace = (workspace = {}) => {
  const accountType = String(workspace?.accountType || "").toUpperCase();
  if (accountType === "PERSONAL" || accountType === "INDIVIDUAL") return true;
  return Boolean(workspace?.id) && accountType !== "ORGANIZATION" && !workspace?.organizationId;
};

const careProfileId = (careProfile = {}) =>
  careProfile?.id || careProfile?.careProfileId || careProfile?.profileId || "";

const normalizeCareProfile = (result) => {
  if (Array.isArray(result)) return result[0] || null;
  if (Array.isArray(result?.content)) return result.content[0] || null;
  if (Array.isArray(result?.data)) return result.data[0] || null;
  return result || null;
};

const oneLineAddress = (address = {}) =>
  [address.street, address.addressLine1, address.city, address.state, address.zipCode, address.country]
    .filter(Boolean)
    .join(", ");

const structuredAddress = (value) => {
  if (!value) return { search: "", addressLine1: "", city: "", county: "", state: "", country: "", zipCode: "", placeId: "" };
  if (typeof value === "string") {
    return { search: value, addressLine1: value, city: "", county: "", state: "", country: "", zipCode: "", placeId: "" };
  }
  return {
    search: value.search || value.formattedAddress || value.address || value.addressLine1 || "",
    addressLine1: value.addressLine1 || value.street || value.address || "",
    city: value.city || "",
    county: value.county || "",
    state: value.state || "",
    country: value.country || "",
    zipCode: value.zipCode || value.zip || "",
    placeId: value.placeId || "",
    latitude: value.latitude,
    longitude: value.longitude,
  };
};

const addressSummary = (address) => {
  const value = structuredAddress(address);
  return [value.addressLine1, value.city, value.county, value.state, value.country, value.zipCode]
    .filter(Boolean)
    .join(", ");
};

const profileName = (profile = {}) =>
  [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ").trim() ||
  profile.fullName ||
  profile.name ||
  "";

const userProfileSeed = (profile = {}, authUser = {}) => ({
  firstName: profile.firstName || authUser.firstName || "",
  middleName: profile.middleName || authUser.middleName || "",
  lastName: profile.lastName || authUser.lastName || "",
  email:
    profile.email ||
    profile.contactDetails?.personalEmail ||
    profile.contactDetails?.email ||
    authUser.email ||
    "",
  phone:
    profile.phone ||
    profile.contactDetails?.mobilePhone ||
    profile.contactDetails?.workPhone ||
    authUser.phone ||
    "",
  birthDate: profile.birthDate || profile.dateOfBirth || "",
  gender: profile.gender || "",
  preferredContactMethod: profile.preferredContactMethod || "",
  address: oneLineAddress(profile.residentialAddress || profile.permanentAddress || profile.address),
});

const careSeedFromCareProfile = (careProfile = {}) => ({
  careRole: careProfile.careRole || careProfile.care?.careRole || "",
  desiredEmploymentType: careProfile.desiredEmploymentType || careProfile.care?.desiredEmploymentType || careProfile.employmentType || careProfile.care?.employmentType || "",
  workAuthorization: careProfile.workAuthorization || careProfile.care?.workAuthorization || "",
  availability: careProfile.availability || careProfile.care?.availability || "",
  serviceArea: careProfile.serviceArea || careProfile.care?.serviceArea || "",
  travelRadius: careProfile.travelRadius || careProfile.care?.travelRadius || "",
  preferredShifts: toList(careProfile.preferredShifts || careProfile.care?.preferredShifts),
  preferredHoursPerWeek: careProfile.preferredHoursPerWeek || careProfile.care?.preferredHoursPerWeek || "",
  canWorkOn: toList(careProfile.canWorkOn || careProfile.care?.canWorkOn || careProfile.canWorkWeekends || careProfile.care?.canWorkWeekends),
  careSpecialties: careProfile.careSpecialties || careProfile.care?.careSpecialties || "",
  clientTypes: careProfile.clientTypes || careProfile.care?.clientTypes || "",
  homeCareServices: careProfile.homeCareServices || careProfile.care?.homeCareServices || "",
  transportation: careProfile.transportation || careProfile.care?.transportation || "",
  languages: toList(careProfile.languages || careProfile.care?.languages),
  otherLanguage: careProfile.otherLanguage || careProfile.care?.otherLanguage || "",
  disability: careProfile.disability || careProfile.care?.disability || "",
  disabilityExplanation: careProfile.disabilityExplanation || careProfile.care?.disabilityExplanation || "",
  chronicIllness: careProfile.chronicIllness || careProfile.care?.chronicIllness || "",
  chronicIllnessExplanation: careProfile.chronicIllnessExplanation || careProfile.care?.chronicIllnessExplanation || "",
  allergies: careProfile.allergies || careProfile.care?.allergies || "",
  permanentAddress: structuredAddress(careProfile.permanentAddress || careProfile.care?.permanentAddress),
  residentialAddress: structuredAddress(careProfile.residentialAddress || careProfile.care?.residentialAddress),
  nextOfKinName: careProfile.nextOfKinName || careProfile.care?.nextOfKinName || "",
  nextOfKinRelationship: careProfile.nextOfKinRelationship || careProfile.care?.nextOfKinRelationship || "",
  nextOfKinPhone: careProfile.nextOfKinPhone || careProfile.care?.nextOfKinPhone || "",
  nextOfKinEmail: careProfile.nextOfKinEmail || careProfile.care?.nextOfKinEmail || "",
  emergencyContactName: careProfile.emergencyContactName || careProfile.care?.emergencyContactName || "",
  emergencyContactRelationship: careProfile.emergencyContactRelationship || careProfile.care?.emergencyContactRelationship || "",
  emergencyContactPhone: careProfile.emergencyContactPhone || careProfile.care?.emergencyContactPhone || "",
  emergencyContactEmail: careProfile.emergencyContactEmail || careProfile.care?.emergencyContactEmail || "",
  hobbies: careProfile.hobbies || careProfile.care?.hobbies || "",
  caregiverStatement: careProfile.caregiverStatement || careProfile.care?.caregiverStatement || "",
  notes: careProfile.notes || careProfile.care?.notes || "",
});

const professionalSeed = (profile = {}) => ({
  professionalProfileId: profile.id || profile.professionProfileId || profile.profileId || "",
  professionalProfileName: profileTitle(profile),
  sector: profile.sector?.name || profile.sectorCode || profile.sector || "",
  profileSummary: profile.profileSummary || "",
  educationLevel: profile.educationLevel || "",
  interestedServices: toList(profile.interestedServices).join(", "),
  interestedLocations: toList(profile.interestedLocations).join(", "),
  interestedShifts: toList(profile.interestedShifts).join(", "),
  certifications: toList(profile.certifications || profile.licenses || profile.certificates)
    .map((item) => item.name || item.title || item.credentialName || item)
    .filter(Boolean)
    .join(", "),
});

const professionalProfileId = (profile = {}) =>
  profile.id || profile.professionProfileId || profile.profileId || "";

const workAuthorizationFromProfile = (profile = {}) =>
  normalizeWorkAuthorizationUrls(
    profile.userWorkAuthorization || profile.workAuthorization || profile.workAuthorizationDetails || null,
  );

const normalizeWorkAuthorizationUrls = (workAuthorization = null) => {
  if (!workAuthorization) return null;
  const idDocumentUrls = workAuthorizationDocumentUrls(workAuthorization);
  if (idDocumentUrls.length) return workAuthorization;

  const legacyUrls = toList(workAuthorization?.idDocumentIds)
    .map(clean)
    .filter((value) => /^https?:\/\//i.test(value));

  return legacyUrls.length
    ? { ...workAuthorization, idDocumentUrls: legacyUrls }
    : workAuthorization;
};

const identityVerificationFromProfile = (profile = {}) =>
  profile.userIdentityVerification || profile.identityVerification || profile.identificationDocumentVerification || null;

const workAuthorizationType = (workAuthorization = {}) =>
  workAuthorization?.workAuthorizationType || workAuthorization?.type || "";

const hasWorkAuthorizationDocument = (workAuthorization = {}) =>
  Boolean(
    toList(workAuthorization?.idDocumentUrls).some((url) => clean(url)) ||
    toList(workAuthorization?.idDocumentIds).some((id) => clean(id)) ||
    toList(workAuthorization?.documentIds).some((id) => clean(id)) ||
    toList(workAuthorization?.documentPhotos).length ||
    toList(workAuthorization?.documentFiles).length ||
    clean(workAuthorization?.idDocumentId) ||
    clean(workAuthorization?.idDocumentIds) ||
    clean(workAuthorization?.documentId) ||
    clean(workAuthorization?.frontPhoto) ||
    clean(workAuthorization?.backPhoto) ||
    workAuthorization?.uploadedOn,
  );

const workAuthorizationDocumentIds = (workAuthorization = {}) => [
  ...toList(workAuthorization?.idDocumentIds),
  ...toList(workAuthorization?.documentIds),
  clean(workAuthorization?.idDocumentId),
  clean(workAuthorization?.documentId),
].filter(Boolean);

const workAuthorizationDocumentUrls = (workAuthorization = {}) =>
  toList(workAuthorization?.idDocumentUrls).map(clean).filter(Boolean);

const identityDocumentUrls = (identityVerification = {}) =>
  toList(identityVerification?.idDocumentUrls).map(clean).filter(Boolean);

const workAuthorizationIsVerified = (workAuthorization = {}) =>
  Boolean(workAuthorization?.isVerified || workAuthorization?.verified);

const identityVerificationIsVerified = (identityVerification = {}) =>
  Boolean(identityVerification?.isVerified || identityVerification?.verified);

const booleanLabel = (value) =>
  typeof value === "boolean" ? (value ? "Yes" : "No") : "N/A";

const dateTimeLabel = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const workAuthorizationPageIsImage = (page = {}) => {
  const contentType = String(page?.metadata?.contentType || page?.metadata?.mimeType || "").toLowerCase();
  const filename = String(page?.metadata?.filename || page?.metadata?.name || page?.signedUrl || "").toLowerCase();
  return contentType.startsWith("image/") || /\.(jpg|jpeg|png|webp|gif|heic|heif)(\?|$)/i.test(filename);
};

const workAuthorizationConfigForType = (type) => {
  if (type === "NOT_AUTHORIZED") {
    return { capability: "NO", requiresVerification: false, hasRestrictions: true };
  }
  if (type === "US_CITIZEN" || type === "PERMANENT_RESIDENT") {
    return { capability: "YES", requiresVerification: false, hasRestrictions: false };
  }
  if (manualReviewWorkAuthorizationTypes.has(type)) {
    return { capability: "CONDITIONAL", requiresVerification: true, hasRestrictions: true };
  }
  return { capability: "CONDITIONAL", requiresVerification: true, hasRestrictions: false };
};

const workAuthorizationSummaryItems = (workAuthorization = {}) => [
  ["Authorization Document", hasWorkAuthorizationDocument(workAuthorization) ? "Uploaded" : "Not uploaded", hasWorkAuthorizationDocument(workAuthorization) ? "good" : "warn"],
  ["Capability", titleCase(workAuthorization?.capability) || "Not provided"],
  ["Authorization Type", titleCase(workAuthorizationType(workAuthorization)) || "N/A"],
  ["Can Work", booleanLabel(workAuthorization?.canWork)],
  ["Requires Verification", booleanLabel(workAuthorization?.requiresVerification)],
  ["Has Restrictions", booleanLabel(workAuthorization?.hasRestrictions)],
  ["Expiration Date", workAuthorization?.expirationDate || "N/A"],
  ...(workAuthorization?.notes ? [["Notes", workAuthorization.notes]] : []),
  ["Uploaded On", dateTimeLabel(workAuthorization?.uploadedOn)],
  ...(workAuthorizationIsVerified(workAuthorization) ? [["Verified On", dateTimeLabel(workAuthorization?.verifiedOn)]] : []),
];

const identityVerificationSummaryItems = (identityVerification = {}) => [
  ["Identification Document", identityDocumentUrls(identityVerification).length ? "Uploaded" : "Not uploaded", identityDocumentUrls(identityVerification).length ? "good" : "warn"],
  ["Document Type", titleCase(identityVerification?.identificationDocumentType) || "N/A"],
  ["Decision", titleCase(identityVerification?.verificationDecision) || "N/A"],
  ["Requires Review", booleanLabel(identityVerification?.requiresReview)],
  ["Uploaded On", dateTimeLabel(identityVerification?.uploadedOn)],
  ["Validated On", dateTimeLabel(identityVerification?.validatedOn)],
  ...(identityVerificationIsVerified(identityVerification) ? [["Verified On", dateTimeLabel(identityVerification?.verifiedOn)]] : []),
];


const emptyProfessional = {
  professionalProfileId: "",
  professionalProfileName: "",
  sector: "",
  profileSummary: "",
  educationLevel: "",
  interestedServices: "",
  interestedLocations: "",
  interestedShifts: "",
  certifications: "",
};

const emptyCare = {
  careRole: "",
  desiredEmploymentType: "",
  workAuthorization: "",
  availability: "",
  serviceArea: "",
  travelRadius: "",
  preferredShifts: [],
  preferredHoursPerWeek: "",
  canWorkOn: [],
  careSpecialties: "",
  clientTypes: "",
  homeCareServices: "",
  transportation: "",
  languages: [],
  otherLanguage: "",
  disability: "",
  disabilityExplanation: "",
  chronicIllness: "",
  chronicIllnessExplanation: "",
  allergies: "",
  permanentAddress: structuredAddress(),
  residentialAddress: structuredAddress(),
  nextOfKinName: "",
  nextOfKinRelationship: "",
  nextOfKinPhone: "",
  nextOfKinEmail: "",
  emergencyContactName: "",
  emergencyContactRelationship: "",
  emergencyContactPhone: "",
  emergencyContactEmail: "",
  hobbies: "",
  caregiverStatement: "",
  notes: "",
};

export default function CareProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedStepKey = searchParams.get("step") || "";
  const { user } = useAuth();
  const actorId = currentUserId(user);
  const {
    selectedWorkspaceId,
    selectedWorkspace,
    workspaces,
    selectWorkspace,
  } = useWorkspace();
  const { appConfig, loading: appConfigLoading } = useAppConfig();
  const googleMapsApiKey =
    appConfig?.googleMapsApiKey ||
    appConfig?.googleMapsApiMatrixKey ||
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.VITE_GOOGLE_MAPS_API_KEY ||
    "";
  const { memberships, loading: membersLoading } = useWorkspaceMembers({
    enabled: Boolean(actorId && selectedWorkspaceId),
    size: 500,
  });
  const { getUserById } = useUsers(0, 20, null, true, { autoFetch: false });
  const {
    uploadFile,
    submitWorkAuthorization,
    uploading: uploadingWorkAuthorization,
  } = useFileUpload();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [careProfile, setCareProfile] = useState(null);
  const [profileIdentityVerification, setProfileIdentityVerification] = useState(null);
  const [identityDocumentPages, setIdentityDocumentPages] = useState([]);
  const [profileWorkAuthorization, setProfileWorkAuthorization] = useState(null);
  const [workAuthorizationPages, setWorkAuthorizationPages] = useState([]);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [documentPreviewRotation, setDocumentPreviewRotation] = useState(0);
  const [relatedDrawer, setRelatedDrawer] = useState("");
  const [workAuthorizationEditing, setWorkAuthorizationEditing] = useState(false);
  const [workAuthorizationSubmitting, setWorkAuthorizationSubmitting] = useState(false);
  const [workAuthorizationForm, setWorkAuthorizationForm] = useState({
    type: "",
    expirationDate: "",
    notes: "",
    attestedAuthorized: false,
  });
  const [workAuthorizationFiles, setWorkAuthorizationFiles] = useState([]);
  const [professionalProfiles, setProfessionalProfiles] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [unlockedStepIndex, setUnlockedStepIndex] = useState(0);
  const [profileForm, setProfileForm] = useState(userProfileSeed({}, user));
  const [professionalForm, setProfessionalForm] = useState(emptyProfessional);
  const [careForm, setCareForm] = useState(emptyCare);

  const personalWorkspaces = useMemo(
    () => workspaces.filter(isPersonalWorkspace),
    [workspaces],
  );
  const selectedWorkspaceIsPersonal = isPersonalWorkspace(selectedWorkspace);

  const openProfessionalProfiles = useCallback(
    (workspaceId) => {
      if (workspaceId && workspaceId !== selectedWorkspaceId) {
        selectWorkspace(workspaceId);
      }
      navigate(professionalProfilesPath);
    },
    [navigate, selectWorkspace, selectedWorkspaceId],
  );

  const createPersonalWorkspace = useCallback(() => {
    navigate(paths.workspaceCreate(), {
      state: {
        accountType: "PERSONAL",
        nextPath: professionalProfilesPath,
      },
    });
  }, [navigate]);

  const acceptedMembership = useMemo(
    () =>
      memberships.find((membership) => {
        const memberUser = getWorkspaceMemberUser(membership);
        return memberUser?.id && actorId && memberUser.id === actorId;
      }),
    [actorId, memberships],
  );

  const workspaceMember = useMemo(
    () => getWorkspaceMemberUser(acceptedMembership) || null,
    [acceptedMembership],
  );

  const loadCareProfile = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const [identityResult, careResult, professionalResult] = await Promise.allSettled([
        getUserById(actorId),
        careService.careProfiles.getMine({
          userId: actorId,
          workspaceId: selectedWorkspaceId,
        }),
        olreadiaService.professionProfiles.listByUser(actorId, {
          page: 0,
          size: 50,
        }, { actorId, skipWorkspaceContext: true }),
      ]);

      const identityProfile =
        identityResult.status === "fulfilled" ? identityResult.value : {};
      const loadedCareProfile =
        careResult.status === "fulfilled" ? normalizeCareProfile(careResult.value) : null;
      const profileList =
        professionalResult.status === "fulfilled" ? toList(professionalResult.value) : [];

      const loadedWorkAuthorization = workAuthorizationFromProfile(identityProfile);
      const loadedIdentityVerification = identityVerificationFromProfile(identityProfile);
      setCareProfile(loadedCareProfile);
      setProfileIdentityVerification(loadedIdentityVerification);
      setProfileWorkAuthorization(loadedWorkAuthorization);
      setWorkAuthorizationForm({
        type: workAuthorizationType(loadedWorkAuthorization),
        expirationDate: loadedWorkAuthorization?.expirationDate || "",
        notes: loadedWorkAuthorization?.notes || "",
        attestedAuthorized: false,
      });
      setWorkAuthorizationFiles([]);
      setProfessionalProfiles(profileList);

      setProfileForm(userProfileSeed(identityProfile, user));

      if (loadedCareProfile?.professionalProfileId) {
        const selected = profileList.find((item) => professionalProfileId(item) === loadedCareProfile.professionalProfileId);
        setProfessionalForm(selected ? professionalSeed(selected) : {
          ...emptyProfessional,
          professionalProfileId: loadedCareProfile.professionalProfileId,
        });
      } else {
        setProfessionalForm(profileList[0] ? professionalSeed(profileList[0]) : emptyProfessional);
      }

      setCareForm(loadedCareProfile ? { ...emptyCare, ...careSeedFromCareProfile(loadedCareProfile) } : emptyCare);

      const loadedStatus = String(loadedCareProfile?.onboardingStatus || loadedCareProfile?.status || "").toUpperCase();
      const loadedComplete = ["COMPLETE", "COMPLETED", "ACTIVE", "APPROVED"].includes(loadedStatus);
      const loadedStageIndex = stepIndexForKey(loadedCareProfile?.onboardingStage || loadedCareProfile?.stepKey);
      const nextUnlockedStepIndex = loadedComplete
        ? steps.length - 1
        : loadedCareProfile
          ? Math.min(Math.max(loadedStageIndex + 1, stepIndexForKey("care")), steps.length - 1)
          : 0;
      const requestedStepIndex = stepIndexForKey(requestedStepKey);
      const shouldUseRequestedStep = requestedStepKey && requestedStepIndex <= nextUnlockedStepIndex + 1;
      const nextStepIndex = shouldUseRequestedStep
        ? Math.min(requestedStepIndex, steps.length - 1)
        : nextUnlockedStepIndex;
      setUnlockedStepIndex(Math.max(nextUnlockedStepIndex, nextStepIndex));
      setStepIndex(nextStepIndex);
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load Care Profile.");
    } finally {
      setLoading(false);
    }
  }, [actorId, getUserById, requestedStepKey, selectedWorkspaceId, user]);

  useEffect(() => {
    loadCareProfile();
  }, [loadCareProfile]);

  useEffect(() => {
    const documentUrls = identityDocumentUrls(profileIdentityVerification);
    setIdentityDocumentPages(
      documentUrls.map((signedUrl, index) => ({
        fileId: signedUrl,
        index,
        signedUrl,
      })),
    );
  }, [profileIdentityVerification]);

  useEffect(() => {
    const documentUrls = workAuthorizationDocumentUrls(profileWorkAuthorization);
    setWorkAuthorizationPages(
      documentUrls.map((signedUrl, index) => ({
        fileId: signedUrl,
        index,
        signedUrl,
      })),
    );
  }, [profileWorkAuthorization]);

  const updateCare = (field, value) =>
    setCareForm((current) => ({ ...current, [field]: value }));

  const toggleCareListValue = (field, value) =>
    setCareForm((current) => ({
      ...current,
      [field]: toList(current[field]).includes(value)
        ? toList(current[field]).filter((item) => item !== value)
        : [...toList(current[field]), value],
    }));

  const toggleShift = (shift) => toggleCareListValue("preferredShifts", shift);

  const updateCareAddress = (field, key, value) =>
    setCareForm((current) => ({
      ...current,
      [field]: {
        ...structuredAddress(current[field]),
        [key]: value,
      },
    }));

  const applyCareAddress = (field, nextAddress) =>
    setCareForm((current) => ({
      ...current,
      [field]: {
        ...structuredAddress(current[field]),
        ...nextAddress,
      },
    }));

  const selectProfessionalProfile = (profileId) => {
    const selected = professionalProfiles.find((profile) => professionalProfileId(profile) === profileId);
    setProfessionalForm(selected ? professionalSeed(selected) : emptyProfessional);
  };

  const updateWorkAuthorizationForm = (field, value) =>
    setWorkAuthorizationForm((current) => ({ ...current, [field]: value }));

  const pageLabel = (index) =>
    index === 0 ? "Front / First Page" : index === 1 ? "Back / Second Page" : `Extra Page ${index + 1}`;

  const addWorkAuthorizationFiles = (fileList) => {
    const nextFiles = Array.from(fileList || []).map((file) => ({
      file,
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      previewUrl: file.type?.startsWith("image/") ? URL.createObjectURL(file) : "",
    }));
    setWorkAuthorizationFiles((current) => [...current, ...nextFiles]);
  };

  const removeWorkAuthorizationFile = (fileId) => {
    setWorkAuthorizationFiles((current) => {
      const target = current.find((item) => item.id === fileId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return current.filter((item) => item.id !== fileId);
    });
  };

  const startWorkAuthorizationEdit = () => {
    setWorkAuthorizationForm({
      type: workAuthorizationType(profileWorkAuthorization),
      expirationDate: profileWorkAuthorization?.expirationDate || "",
      notes: profileWorkAuthorization?.notes || "",
      attestedAuthorized: false,
    });
    setWorkAuthorizationFiles([]);
    setWorkAuthorizationEditing(true);
    setSaveError("");
  };

  const cancelWorkAuthorizationEdit = () => {
    workAuthorizationFiles.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });
    setWorkAuthorizationFiles([]);
    setWorkAuthorizationEditing(false);
    setSaveError("");
  };

  const submitWorkAuthorizationUpdate = async () => {
    const type = workAuthorizationForm.type;
    if (!type) {
      setSaveError("Please select a work authorization type.");
      return;
    }
    if (!workAuthorizationForm.attestedAuthorized) {
      setSaveError("Please confirm you are authorized to perform work before submitting.");
      return;
    }
    if (type === "NOT_AUTHORIZED") {
      setSaveError("This category indicates you are not currently authorized to work.");
      return;
    }
    if (type !== "US_CITIZEN" && !workAuthorizationForm.expirationDate.trim()) {
      setSaveError("Please provide an expiration date for this work authorization type.");
      return;
    }
    const existingDocumentIds = workAuthorizationDocumentIds(profileWorkAuthorization);
    if (!workAuthorizationFiles.length && !existingDocumentIds.length) {
      setSaveError("Please upload your work authorization document.");
      return;
    }

    const config = workAuthorizationConfigForType(type);
    setWorkAuthorizationSubmitting(true);
    setSaveError("");
    try {
      const uploadedIds = [];
      for (const item of workAuthorizationFiles) {
        const upload = await uploadFile(actorId, item.file, {
          purpose: "USER_PROFILE",
          fileType: "PHOTO",
          preserveOriginal: true,
          includeUserId: false,
        });
        const fileId = upload?.fileId || upload?.file?.id || upload?.file?.fileId || null;
        if (fileId) uploadedIds.push(String(fileId));
      }

      const documentIds = uploadedIds.length ? uploadedIds : existingDocumentIds;
      const payload = {
        capability: config.capability,
        type,
        workAuthorizationType: type,
        canWork: config.capability !== "NO",
        requiresVerification: config.requiresVerification,
        hasRestrictions: config.hasRestrictions,
        expirationDate: workAuthorizationForm.expirationDate.trim() || null,
        notes: workAuthorizationForm.notes.trim() || null,
        idDocumentIds: documentIds,
      };
      const updated = await submitWorkAuthorization(actorId, payload, documentIds);
      setProfileWorkAuthorization(updated?.userWorkAuthorization || updated?.data || updated || payload);
      setWorkAuthorizationEditing(false);
      setWorkAuthorizationFiles([]);
      await loadCareProfile();
    } catch (nextError) {
      setSaveError(nextError?.message || "Failed to submit work authorization.");
    } finally {
      setWorkAuthorizationSubmitting(false);
    }
  };

  const payloadForStep = (stepKey) => {
    const { workAuthorization, ...carePayload } = careForm;
    return {
      userId: actorId,
      workspaceId: selectedWorkspaceId,
      professionalProfileId: professionalForm.professionalProfileId || null,
      care: carePayload,
      stepKey,
      onboardingStatus: stepKey === "review" ? "COMPLETE" : "IN_PROGRESS",
    };
  };

  const saveStep = async (stepKey = steps[stepIndex].key) => {
    if (sourceOnlyStepKeys.has(stepKey)) {
      setSaveError("");
      return careProfile;
    }

    setSaving(true);
    setSaveError("");
    try {
      const payload = payloadForStep(stepKey);
      const existingCareProfileId = careProfileId(careProfile);
      const saved = existingCareProfileId
        ? await careService.careProfiles.saveStep(existingCareProfileId, stepKey, payload)
        : await careService.careProfiles.create(payload);
      setCareProfile(normalizeCareProfile(saved) || careProfile || payload);
      return saved;
    } catch (nextError) {
      const message =
        nextError?.response?.data?.message ||
        nextError?.message ||
        "Unable to save this Care Profile step.";
      setSaveError(message);
      throw nextError;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    try {
      const stepKey = steps[stepIndex].key;
      if (!sourceOnlyStepKeys.has(stepKey)) {
        await saveStep(stepKey);
      }
      setStepIndex((current) => {
        const nextIndex = Math.min(current + 1, steps.length - 1);
        setUnlockedStepIndex((unlocked) => Math.max(unlocked, nextIndex));
        return nextIndex;
      });
    } catch {
      // The inline save error keeps the user on the current step.
    }
  };

  const goBack = () => setStepIndex((current) => Math.max(current - 1, 0));

  const goToStep = (index) => {
    if (index > unlockedStepIndex) return;
    setStepIndex(index);
  };

  const saveCurrentStep = async (stepKey) => {
    try {
      const currentStepKey = stepKey || steps[stepIndex].key;
      await saveStep(currentStepKey);
      if (currentStepKey === steps[steps.length - 1]?.key) {
        refreshApplicationSoon();
        return;
      }
      if (!sourceOnlyStepKeys.has(currentStepKey)) {
        setUnlockedStepIndex((current) => Math.max(current, Math.min(stepIndex + 1, steps.length - 1)));
      }
    } catch {
      // The inline save error keeps the user on the current step.
    }
  };

  const startOnboarding = () => setStepIndex(0);

  const activeStep = steps[stepIndex];
  const hasProfessionalProfiles = professionalProfiles.length > 0;
  const showOnboardingPrompt = acceptedMembership && !careProfile && !loading;
  const careProfileStatus = String(careProfile?.onboardingStatus || careProfile?.status || "").toUpperCase();
  const careProfileComplete = ["COMPLETE", "COMPLETED", "ACTIVE", "APPROVED"].includes(careProfileStatus);
  const finalOnboardingStepComplete = careProfileComplete && activeStep?.key === steps[steps.length - 1]?.key;

  const memberName = workspaceMember ? getWorkspaceMemberDisplayName(workspaceMember) : profileName(user);
  const memberEmail = workspaceMember ? getWorkspaceMemberEmail(workspaceMember) : user?.email;
  const workAuthorizationPageName = (page, index) =>
    page?.metadata?.filename ||
    page?.metadata?.name ||
    page?.metadata?.originalFilename ||
    pageLabel(index);

  const openDocumentPreview = (page, index, title, galleryPages = []) => {
    if (!page?.signedUrl) return;
    setDocumentPreviewRotation(0);
    setDocumentPreview({
      ...page,
      index,
      title,
      label: pageLabel(index),
      name: workAuthorizationPageName(page, index),
      galleryPages,
    });
  };

  const renderDocumentPageButton = (page, index, title, galleryPages) => (
    <button
      className="care-profile-work-auth-page"
      type="button"
      onClick={() => openDocumentPreview(page, index, title, galleryPages)}
      key={page.fileId || `${title}-${index}`}
      title="Preview document"
    >
      <span className="care-profile-work-auth-page__thumb">
        {page.signedUrl && workAuthorizationPageIsImage(page) ? <img src={page.signedUrl} alt="" /> : <i className="ti ti-file-description" />}
      </span>
      <span className="care-profile-work-auth-page__caption">
        <strong>{pageLabel(index)}</strong>
        <small>{workAuthorizationPageName(page, index)}</small>
      </span>
      <i className="ti ti-eye" aria-hidden="true" />
    </button>
  );

  const rotateDocumentPreview = (degrees) =>
    setDocumentPreviewRotation((current) => (current + degrees + 360) % 360);

  const navigateDocumentPreview = (direction) => {
    if (!documentPreview?.galleryPages?.length) return;
    const pages = documentPreview.galleryPages;
    const nextIndex = (documentPreview.index + direction + pages.length) % pages.length;
    openDocumentPreview(pages[nextIndex], nextIndex, documentPreview.title, pages);
  };

  const previewItem = (label, value) => (
    <div className="care-profile-review-item" key={label}>
      <span>{label}</span>
      <strong className={value && typeof value === "object" && !Array.isArray(value) ? "has-action" : ""}>
        {Array.isArray(value) ? value.join(", ") : value || "Not set"}
      </strong>
    </div>
  );

  const reviewSection = (title, icon, items) => (
    <section className="care-profile-review-section" key={title}>
      <div className="care-profile-review-section__head">
        <i className={`ti ${icon}`} />
        <strong>{title}</strong>
      </div>
      <div className="care-profile-review-grid">
        {items.map(([label, value]) => previewItem(label, value))}
      </div>
    </section>
  );

  const linkedProfileId = (value, label, drawerKey) => (
    value ? (
      <span className="care-staff-id-link">
        <span>{value}</span>
        <Tooltip title="Preview in drawer">
          <button className="care-staff-eye-link" type="button" onClick={() => setRelatedDrawer(drawerKey)} aria-label={`Preview ${label}`}>
            <i className="ti ti-eye" />
          </button>
        </Tooltip>
      </span>
    ) : ""
  );

  const renderProfileStep = () => (
    <section className="care-profile-flow-card care-profile-embedded-profile">
      {actorId ? (
        <div className="care-profile-linked-id-row">
          <span>User profile ID</span>
          {linkedProfileId(actorId, "User profile", "user")}
        </div>
      ) : null}
      <UserProfilePage />
    </section>
  );

  const renderProfessionalStep = () => (
    <section className="care-profile-flow-card care-profile-professional-stage">
      <div className="care-profile-professional-guide">
        <div>
          <span className="care-profile-guide-kicker">Professional Profile setup</span>
          <h3>Create your Professional Profile from a personal workspace</h3>
          <p>
            Professional Profiles belong in individual/personal workspaces. Create a personal workspace first,
            then create your Professional Profile. If you already have a personal workspace, switch to it and
            continue from Professional Profiles.
          </p>
        </div>
        <ol>
          <li>Create a personal workspace.</li>
          <li>Switch to that personal workspace.</li>
          <li>Create or update your Professional Profile.</li>
        </ol>
        <div className="care-profile-guide-actions">
          {selectedWorkspaceIsPersonal ? (
            <button className="mini-btn care-profile-primary-action" type="button" onClick={() => openProfessionalProfiles(selectedWorkspaceId)}>
              <i className="ti ti-user-plus" /> Create Professional Profile
            </button>
          ) : null}
          {!selectedWorkspaceIsPersonal && personalWorkspaces.length ? (
            personalWorkspaces.map((workspace) => (
              <button className="mini-btn care-profile-primary-action" type="button" key={workspace.id} onClick={() => openProfessionalProfiles(workspace.id)}>
                <i className="ti ti-switch-horizontal" /> Switch to {workspace.name || "Personal Workspace"}
              </button>
            ))
          ) : null}
          <button className="mini-btn" type="button" onClick={createPersonalWorkspace}>
            <i className="ti ti-plus" /> Create Personal Workspace
          </button>
        </div>
      </div>
      <div className="resident-form-grid compact">
        <label className="resident-form-field full-span">
          Professional profile
          <select
            value={professionalForm.professionalProfileId || ""}
            onChange={(event) => selectProfessionalProfile(event.target.value)}
          >
            <option value="">
              {hasProfessionalProfiles ? "No professional profile selected" : "No professional profile found"}
            </option>
            {professionalProfiles.map((profile) => (
              <option value={professionalProfileId(profile)} key={professionalProfileId(profile)}>
                {profileTitle(profile)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {professionalForm.professionalProfileId ? (
        <div className="care-profile-linked-id-row">
          <span>Professional profile ID</span>
          {linkedProfileId(professionalForm.professionalProfileId, "Professional profile", "professional")}
        </div>
      ) : null}
      {professionalForm.professionalProfileId ? (
        <ProfessionalProfileDetails
          profileIdOverride={professionalForm.professionalProfileId}
          embedded
          hidePromotions
        />
      ) : (
        <p>No professional profile selected.</p>
      )}
    </section>
  );

  const renderAddressFields = (field, title) => {
    const value = structuredAddress(careForm[field]);
    return (
      <div className="resident-address-section full-span is-compact" key={field}>
        <div className="resident-form-section-title">
          <i className="ti ti-map-pin" />
          <span>{title}</span>
        </div>
        <label className="resident-form-field full-span resident-address-search">
          <span>Location search</span>
          {appConfigLoading && !googleMapsApiKey ? (
            <input type="text" disabled placeholder="Loading map address search..." />
          ) : (
            <GooglePlacesAutocompleteInput
              googleMapsApiKey={googleMapsApiKey}
              placeholder={googleMapsApiKey ? "Search address e.g. 100 Main St, Seattle" : "Enter address"}
              types={["address"]}
              value={value.search || value.addressLine1}
              onChange={(nextValue) => {
                updateCareAddress(field, "search", nextValue);
                if (!googleMapsApiKey) updateCareAddress(field, "addressLine1", nextValue);
              }}
              onResolvePlace={async ({ details, prediction }) => {
                const resolvedAddress = details?.formatted_address || prediction?.description || value.search || "";
                applyCareAddress(field, {
                  ...(details ? addressFieldsFromPlaceDetails(details) : {}),
                  search: resolvedAddress,
                  addressLine1: details ? addressFieldsFromPlaceDetails(details).addressLine1 || resolvedAddress : resolvedAddress,
                });
              }}
            />
          )}
        </label>
        <div className="resident-address-grid">
          {addressFields.map(([addressField, label]) => (
            <label className={`resident-form-field resident-address-field resident-address-field-${addressField}`} key={addressField}>
              <span>{label}</span>
              <input
                type="text"
                value={value[addressField] || ""}
                onChange={(event) => updateCareAddress(field, addressField, event.target.value)}
              />
            </label>
          ))}
        </div>
      </div>
    );
  };

  const renderCareStep = () => (
    <div className="care-profile-form-sections">
        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-id" />
            <span>Identification document</span>
          </div>
          <div className="care-profile-work-auth-card">
            <div className="care-profile-work-auth-card__glow-top" />
            <div className="care-profile-work-auth-card__glow-bottom" />
            <div className="care-profile-work-auth-card__header">
              <strong>Identity Verification Summary</strong>
              <span className={`care-profile-work-auth-pill${identityVerificationIsVerified(profileIdentityVerification) ? " verified" : ""}`}>
                {identityVerificationIsVerified(profileIdentityVerification) ? "Verified" : "Not Verified"}
              </span>
            </div>
            <div className="care-profile-work-auth-overview">
              <div className="care-profile-work-auth-summary">
                {identityVerificationSummaryItems(profileIdentityVerification).map(([label, value, tone]) => (
                  <div className={tone ? `is-${tone}` : ""} key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
              <div className="care-profile-work-auth-pages">
                <div className="care-profile-work-auth-pages__head">
                  <strong>Uploaded pages</strong>
                  <small>{identityDocumentPages.length ? `${identityDocumentPages.length} page${identityDocumentPages.length === 1 ? "" : "s"}` : "No uploaded pages"}</small>
                </div>
                {identityDocumentPages.length ? (
                  <div className="care-profile-work-auth-page-grid">
                    {identityDocumentPages.map((page, index) => (
                      renderDocumentPageButton(page, index, "Identification Document", identityDocumentPages)
                    ))}
                  </div>
                ) : (
                  <div className="care-profile-work-auth-pages__empty">
                    <i className="ti ti-file-off" />
                    <span>No uploaded pages found.</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-id-badge-2" />
            <span>Work authorization</span>
          </div>
          <div className="care-profile-work-auth-card">
            <div className="care-profile-work-auth-card__glow-top" />
            <div className="care-profile-work-auth-card__glow-bottom" />
            <div className="care-profile-work-auth-card__header">
              <strong>Authorization Summary</strong>
              <span className={`care-profile-work-auth-pill${workAuthorizationIsVerified(profileWorkAuthorization) ? " verified" : ""}`}>
                {workAuthorizationIsVerified(profileWorkAuthorization) ? "Verified" : "Not Verified"}
              </span>
            </div>
            {!workAuthorizationEditing ? (
              <>
                <div className="care-profile-work-auth-overview">
                  <div className="care-profile-work-auth-summary">
                    {workAuthorizationSummaryItems(profileWorkAuthorization).map(([label, value, tone]) => (
                      <div className={tone ? `is-${tone}` : ""} key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                  <div className="care-profile-work-auth-pages">
                    <div className="care-profile-work-auth-pages__head">
                      <strong>Uploaded pages</strong>
                      <small>{workAuthorizationPages.length ? `${workAuthorizationPages.length} page${workAuthorizationPages.length === 1 ? "" : "s"}` : "No uploaded pages"}</small>
                    </div>
                    {workAuthorizationPages.length ? (
                      <div className="care-profile-work-auth-page-grid">
                        {workAuthorizationPages.map((page, index) => (
                          renderDocumentPageButton(page, index, "Work Authorization Document", workAuthorizationPages)
                        ))}
                      </div>
                    ) : (
                      <div className="care-profile-work-auth-pages__empty">
                        <i className="ti ti-file-off" />
                        <span>No uploaded pages found.</span>
                      </div>
                    )}
                  </div>
                </div>
                <button className="mini-btn care-profile-work-auth-update" type="button" onClick={startWorkAuthorizationEdit}>
                  <i className="ti ti-edit" /> Update Work Eligibility
                </button>
              </>
            ) : (
              <div className="care-profile-work-auth-edit">
                <div className="resident-form-grid compact">
                  <label className="resident-form-field">
                    Work authorization type
                    <select value={workAuthorizationForm.type} onChange={(event) => updateWorkAuthorizationForm("type", event.target.value)}>
                      <option value="">Select Work Authorization Type...</option>
                      {workAuthorizationTypes.map(([value, label]) => (
                        <option value={value} key={value}>{label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="resident-form-field">
                    Expiration date
                    <input type="date" value={workAuthorizationForm.expirationDate} onChange={(event) => updateWorkAuthorizationForm("expirationDate", event.target.value)} />
                  </label>
                  <label className="resident-form-field full-span">
                    Notes
                    <textarea value={workAuthorizationForm.notes} onChange={(event) => updateWorkAuthorizationForm("notes", event.target.value)} placeholder="Any work authorization notes" />
                  </label>
                  <label className="resident-check-card full-span">
                    <input
                      type="checkbox"
                      checked={workAuthorizationForm.attestedAuthorized}
                      onChange={(event) => updateWorkAuthorizationForm("attestedAuthorized", event.target.checked)}
                    />
                    <span>
                      <strong>I confirm I am authorized to perform work through Oliveeb.</strong>
                      <small>This attestation is required before submitting work eligibility documents.</small>
                    </span>
                  </label>
                </div>

                <div className="care-profile-work-auth-upload">
                  <div className="care-profile-work-auth-upload__head">
                    <strong>Authorization documents</strong>
                    <small>{workAuthorizationFiles.length ? `${workAuthorizationFiles.length} selected` : "Upload front and back pages when available."}</small>
                  </div>
                  <label className="care-profile-work-auth-dropzone">
                    <i className="ti ti-camera-plus" />
                    <span>{workAuthorizationFiles.length ? "Add another page" : "Choose or capture document photo"}</span>
                    <small>Images or HEIC/HEIF files</small>
                    <input
                      type="file"
                      accept="image/*,.heic,.heif"
                      multiple
                      onChange={(event) => {
                        addWorkAuthorizationFiles(event.target.files);
                        event.target.value = "";
                      }}
                    />
                  </label>
                  {workAuthorizationFiles.length ? (
                    <div className="care-profile-work-auth-files">
                      {workAuthorizationFiles.map((item, index) => (
                        <div className="care-profile-work-auth-file" key={item.id}>
                          <div className="care-profile-work-auth-file__preview">
                            {item.previewUrl ? <img src={item.previewUrl} alt="" /> : <i className="ti ti-file-description" />}
                          </div>
                          <div>
                            <strong>{pageLabel(index)}</strong>
                            <small>{item.file.name}</small>
                          </div>
                          <button type="button" onClick={() => removeWorkAuthorizationFile(item.id)} aria-label={`Remove ${pageLabel(index)}`}>
                            <i className="ti ti-x" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="care-profile-work-auth-actions">
                  <button className="mini-btn" type="button" onClick={cancelWorkAuthorizationEdit} disabled={workAuthorizationSubmitting || uploadingWorkAuthorization}>
                    <i className="ti ti-x" /> Cancel
                  </button>
                  <button className="mini-btn care-profile-primary-action" type="button" onClick={submitWorkAuthorizationUpdate} disabled={workAuthorizationSubmitting || uploadingWorkAuthorization}>
                    <i className="ti ti-device-floppy" /> {workAuthorizationSubmitting || uploadingWorkAuthorization ? "Submitting..." : "Submit Work Eligibility"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-briefcase" />
            <span>Work availability</span>
          </div>
          <div className="resident-form-grid compact">
            <label className="resident-form-field">
              Preferred hours per week
              <input value={careForm.preferredHoursPerWeek} onChange={(event) => updateCare("preferredHoursPerWeek", event.target.value)} placeholder="e.g. 20-30, 40, flexible" />
            </label>
            <div className="resident-form-field full-span">
              Preferred working shifts
              <div className="care-profile-chip-row">
                {shiftOptions.map((shift) => (
                  <button
                    className={`mini-btn${careForm.preferredShifts.includes(shift) ? " active" : ""}`}
                    type="button"
                    onClick={() => toggleShift(shift)}
                    key={shift}
                  >
                    <i className="ti ti-check" /> {shift}
                  </button>
                ))}
              </div>
            </div>
            <div className="resident-form-field full-span">
              Can work on?
              <div className="care-profile-chip-row">
                {workDayOptions.map((option) => (
                  <button
                    className={`mini-btn${toList(careForm.canWorkOn).includes(option) ? " active" : ""}`}
                    type="button"
                    onClick={() => toggleCareListValue("canWorkOn", option)}
                    key={option}
                  >
                    <i className="ti ti-check" /> {option}
                  </button>
                ))}
              </div>
            </div>
            <label className="resident-form-field">
              Commute distance (miles)
              <input value={careForm.travelRadius} onChange={(event) => updateCare("travelRadius", event.target.value)} placeholder="e.g. 10 miles, 30 minutes" />
            </label>
            <label className="resident-form-field">
              Transportation
              <select value={careForm.transportation} onChange={(event) => updateCare("transportation", event.target.value)}>
                <option value="">Choose answer</option>
                <option value="OWN_CAR">Own car</option>
                <option value="PUBLIC_TRANSPORT">Public transport</option>
                <option value="RIDE_SHARE">Ride share</option>
                <option value="OTHER">Other</option>
              </select>
            </label>
          </div>
        </section>

        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-map-pin" />
            <span>Addresses</span>
          </div>
          <div className="resident-form-grid compact">
            {renderAddressFields("permanentAddress", "Permanent address")}
            {renderAddressFields("residentialAddress", "Residential address")}
          </div>
        </section>

        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-address-book" />
            <span>Next of kin</span>
          </div>
          <div className="resident-form-grid compact">
            <label className="resident-form-field">
              Next of kin name
              <input value={careForm.nextOfKinName} onChange={(event) => updateCare("nextOfKinName", event.target.value)} />
            </label>
            <label className="resident-form-field">
              Next of kin relationship
              <input value={careForm.nextOfKinRelationship} onChange={(event) => updateCare("nextOfKinRelationship", event.target.value)} />
            </label>
            <label className="resident-form-field">
              Next of kin phone
              <input value={careForm.nextOfKinPhone} onChange={(event) => updateCare("nextOfKinPhone", event.target.value)} />
            </label>
            <label className="resident-form-field">
              Next of kin email
              <input type="email" value={careForm.nextOfKinEmail} onChange={(event) => updateCare("nextOfKinEmail", event.target.value)} />
            </label>
          </div>
        </section>

        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-phone-call" />
            <span>Emergency contact</span>
          </div>
          <div className="resident-form-grid compact">
            <label className="resident-form-field">
              Emergency contact name
              <input value={careForm.emergencyContactName} onChange={(event) => updateCare("emergencyContactName", event.target.value)} />
            </label>
            <label className="resident-form-field">
              Emergency contact relationship
              <input value={careForm.emergencyContactRelationship} onChange={(event) => updateCare("emergencyContactRelationship", event.target.value)} />
            </label>
            <label className="resident-form-field">
              Emergency contact phone
              <input value={careForm.emergencyContactPhone} onChange={(event) => updateCare("emergencyContactPhone", event.target.value)} />
            </label>
            <label className="resident-form-field">
              Emergency contact email
              <input type="email" value={careForm.emergencyContactEmail} onChange={(event) => updateCare("emergencyContactEmail", event.target.value)} />
            </label>
          </div>
        </section>

        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-heartbeat" />
            <span>Health information</span>
          </div>
          <div className="resident-form-grid compact">
            <label className="resident-form-field">
              Do you have a disability?
              <select value={careForm.disability} onChange={(event) => updateCare("disability", event.target.value)}>
                <option value="">Choose answer</option>
                <option value="NO">No</option>
                <option value="YES">Yes</option>
                <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
              </select>
            </label>
            <label className="resident-form-field">
              Any chronic illness?
              <select value={careForm.chronicIllness} onChange={(event) => updateCare("chronicIllness", event.target.value)}>
                <option value="">Choose answer</option>
                <option value="NO">No</option>
                <option value="YES">Yes</option>
                <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
              </select>
            </label>
            {careForm.disability === "YES" ? (
              <label className="resident-form-field full-span">
                Disability details
                <textarea value={careForm.disabilityExplanation} onChange={(event) => updateCare("disabilityExplanation", event.target.value)} />
              </label>
            ) : null}
            {careForm.chronicIllness === "YES" ? (
              <label className="resident-form-field full-span">
                Chronic illness details
                <textarea value={careForm.chronicIllnessExplanation} onChange={(event) => updateCare("chronicIllnessExplanation", event.target.value)} />
              </label>
            ) : null}
            <label className="resident-form-field full-span">
              Allergies or health considerations
              <textarea value={careForm.allergies} onChange={(event) => updateCare("allergies", event.target.value)} />
            </label>
          </div>
        </section>

        <section className="resident-form-card care-profile-flow-card">
          <div className="resident-form-subsection full-span">
            <i className="ti ti-user-heart" />
            <span>Personal details</span>
          </div>
          <div className="resident-form-grid compact">
            <div className="resident-form-field full-span">
              Languages spoken
              <div className="care-profile-chip-row">
                {languageOptions.map((language) => (
                  <button
                    className={`mini-btn${toList(careForm.languages).includes(language) ? " active" : ""}`}
                    type="button"
                    onClick={() => toggleCareListValue("languages", language)}
                    key={language}
                  >
                    <i className="ti ti-check" /> {language}
                  </button>
                ))}
              </div>
            </div>
            {toList(careForm.languages).includes("Other") ? (
              <label className="resident-form-field full-span">
                Specify other language
                <input value={careForm.otherLanguage} onChange={(event) => updateCare("otherLanguage", event.target.value)} />
              </label>
            ) : null}
            <label className="resident-form-field full-span">
              Hobbies and interests
              <textarea value={careForm.hobbies} onChange={(event) => updateCare("hobbies", event.target.value)} />
            </label>
            <label className="resident-form-field full-span">
              Availability notes
              <textarea value={careForm.availability} onChange={(event) => updateCare("availability", event.target.value)} />
            </label>
            <label className="resident-form-field full-span">
              Caregiver statement
              <textarea value={careForm.caregiverStatement} onChange={(event) => updateCare("caregiverStatement", event.target.value)} />
            </label>
            <label className="resident-form-field full-span">
              Additional notes
              <textarea value={careForm.notes} onChange={(event) => updateCare("notes", event.target.value)} />
            </label>
          </div>
        </section>
    </div>
  );

  const renderReviewStep = () => (
    <section className="resident-form-card care-profile-flow-card">
      <h3>Review Care Profile</h3>
      <div className="care-profile-review-sections">
        {[
          reviewSection("Profile", "ti-user", [
            ["User profile ID", linkedProfileId(actorId, "User profile", "user")],
          ]),
          reviewSection("Professional Profile", "ti-briefcase", [
            ["Profile ID", linkedProfileId(professionalForm.professionalProfileId, "Professional profile", "professional")],
          ]),
          reviewSection("Identity Verification", "ti-id", [
            ["Identification document", identityDocumentPages.length ? `${identityDocumentPages.length} uploaded page${identityDocumentPages.length === 1 ? "" : "s"}` : "Not uploaded"],
            ["Document type", titleCase(profileIdentityVerification?.identificationDocumentType)],
            ["Decision", titleCase(profileIdentityVerification?.verificationDecision)],
            ["Verified", identityVerificationIsVerified(profileIdentityVerification) ? "Yes" : "No"],
            ["Requires review", booleanLabel(profileIdentityVerification?.requiresReview)],
            ["Uploaded on", dateTimeLabel(profileIdentityVerification?.uploadedOn)],
            ["Validated on", dateTimeLabel(profileIdentityVerification?.validatedOn)],
          ]),
          reviewSection("Work Authorization", "ti-id-badge-2", [
            ["Authorization document", workAuthorizationPages.length ? `${workAuthorizationPages.length} uploaded page${workAuthorizationPages.length === 1 ? "" : "s"}` : "Not uploaded"],
            ["Authorization type", titleCase(workAuthorizationType(profileWorkAuthorization))],
            ["Capability", titleCase(profileWorkAuthorization?.capability)],
            ["Can work", booleanLabel(profileWorkAuthorization?.canWork)],
            ["Requires verification", booleanLabel(profileWorkAuthorization?.requiresVerification)],
            ["Has restrictions", booleanLabel(profileWorkAuthorization?.hasRestrictions)],
            ["Expiration date", profileWorkAuthorization?.expirationDate],
            ["Notes", profileWorkAuthorization?.notes],
            ["Uploaded on", dateTimeLabel(profileWorkAuthorization?.uploadedOn)],
          ]),
          reviewSection("Availability & Work Preferences", "ti-calendar-time", [
            ["Preferred shifts", toList(careForm.preferredShifts).join(", ")],
            ["Preferred hours per week", careForm.preferredHoursPerWeek],
            ["Can work on", toList(careForm.canWorkOn).join(", ")],
            ["Commute distance (miles)", careForm.travelRadius],
            ["Transportation", titleCase(careForm.transportation)],
            ["Availability notes", careForm.availability],
          ]),
          reviewSection("Addresses", "ti-map-pin", [
            ["Permanent address", addressSummary(careForm.permanentAddress)],
            ["Residential address", addressSummary(careForm.residentialAddress)],
          ]),
          reviewSection("Contacts", "ti-address-book", [
            ["Next of kin name", careForm.nextOfKinName],
            ["Next of kin relationship", careForm.nextOfKinRelationship],
            ["Next of kin phone", careForm.nextOfKinPhone],
            ["Next of kin email", careForm.nextOfKinEmail],
            ["Emergency contact name", careForm.emergencyContactName],
            ["Emergency contact relationship", careForm.emergencyContactRelationship],
            ["Emergency contact phone", careForm.emergencyContactPhone],
            ["Emergency contact email", careForm.emergencyContactEmail],
          ]),
          reviewSection("Health & Personal", "ti-heart-pulse", [
            ["Disability", careForm.disability === "YES" ? careForm.disabilityExplanation || "Yes" : titleCase(careForm.disability)],
            ["Chronic illness", careForm.chronicIllness === "YES" ? careForm.chronicIllnessExplanation || "Yes" : titleCase(careForm.chronicIllness)],
            ["Allergies", careForm.allergies],
            ["Languages", [...toList(careForm.languages).filter((item) => item !== "Other"), careForm.otherLanguage].filter(Boolean).join(", ")],
            ["Hobbies", careForm.hobbies],
            ["Caregiver statement", careForm.caregiverStatement],
            ["Additional notes", careForm.notes],
          ]),
          reviewSection("Workspace", "ti-building", [
            ["Workspace", selectedWorkspace?.name || selectedWorkspaceId],
            ["Status", careProfile ? titleCase(careProfile.onboardingStatus || "in progress") : "Not submitted"],
          ]),
        ]}
      </div>
    </section>
  );

  const renderStep = () => {
    if (activeStep.key === "profile") return renderProfileStep();
    if (activeStep.key === "professional") return renderProfessionalStep();
    if (activeStep.key === "care") return renderCareStep();
    return renderReviewStep();
  };

  return (
    <CareShell className="dashboard-content care-profile-page">
      <div className="page-title">
        <h1>My Care Profile</h1>
        <p>Manage your care-service profile for this workspace.</p>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="dashboard-grid full-width-grid care-profile-grid">
        <Panel title="Care Profile Status">
          <div className="care-profile-status-card">
            <span className="round-icon mint"><i className="ti ti-heart-handshake" /></span>
            <div>
              <strong>{memberName || "Workspace member"}</strong>
              <small>{memberEmail || "No email available"}</small>
              <em className={`pill ${careProfileComplete ? "green" : careProfile ? "blue" : "amber"}`}>
                {loading || membersLoading
                  ? "Loading"
                  : careProfile
                    ? titleCase(careProfile.onboardingStatus || careProfile.status || "In progress")
                    : showOnboardingPrompt
                      ? "Onboarding required"
                      : "Not available"}
              </em>
            </div>
          </div>
          {!selectedWorkspaceId ? <p>Select a workspace to manage your Care Profile.</p> : null}
          {selectedWorkspaceId && !acceptedMembership && !membersLoading ? (
            <p>Your workspace membership must be accepted before a Care Profile can be created.</p>
          ) : null}
          {showOnboardingPrompt ? (
            <button className="mini-btn care-profile-primary-action" type="button" onClick={startOnboarding}>
              <i className="ti ti-clipboard-check" /> Start onboarding
            </button>
          ) : null}
          {careProfile ? (
            <button className="mini-btn" type="button" onClick={startOnboarding}>
              <i className="ti ti-edit" /> Edit Care Profile
            </button>
          ) : null}
        </Panel>

        <Panel title="Onboarding Flow">
          <div className="care-profile-stepper">
            {steps.map((step, index) => {
              const locked = index > unlockedStepIndex;
              return (
                <button
                  className={`care-profile-step${index === stepIndex ? " active" : ""}${index < unlockedStepIndex || (careProfileComplete && step.key === "review") ? " done" : ""}${careProfileComplete && step.key === "review" ? " complete" : ""}${locked ? " locked" : ""}`}
                  type="button"
                  onClick={() => goToStep(index)}
                  disabled={locked}
                  aria-disabled={locked}
                  key={step.key}
                >
                  <span>{index + 1}</span>
                  {step.label}
                </button>
              );
            })}
          </div>
          <div className="care-profile-step-content">
            {loading ? <p>Loading Care Profile...</p> : renderStep()}
          </div>
          {saveError ? <p className="form-error">{saveError}</p> : null}
          <div className="care-profile-actions">
            <button className="mini-btn" type="button" onClick={goBack} disabled={stepIndex === 0 || saving}>
              <i className="ti ti-arrow-left" /> Back
            </button>
            <button className="mini-btn" type="button" onClick={() => saveCurrentStep()} disabled={saving || !acceptedMembership || sourceOnlyStepKeys.has(activeStep.key)}>
              <i className="ti ti-device-floppy" /> {saving ? "Saving..." : "Save step"}
            </button>
            {stepIndex < steps.length - 1 ? (
              <button className="mini-btn" type="button" onClick={goNext} disabled={saving || !acceptedMembership}>
                Next <i className="ti ti-arrow-right" />
              </button>
            ) : (
              <button className="mini-btn" type="button" onClick={() => saveCurrentStep("review")} disabled={saving || !acceptedMembership || finalOnboardingStepComplete}>
                <i className="ti ti-check" /> {finalOnboardingStepComplete ? "Completed" : "Complete"}
              </button>
            )}
          </div>
        </Panel>
      </div>

      <Drawer
        open={relatedDrawer === "user"}
        onClose={() => setRelatedDrawer("")}
        title="User Profile Details"
        width="min(860px, 100vw)"
        className="olreadia-flow-drawer care-staff-related-drawer"
        destroyOnClose
      >
        {actorId ? <UserProfilePage userIdOverride={actorId} embedded readOnly /> : null}
      </Drawer>

      <Drawer
        open={relatedDrawer === "professional"}
        onClose={() => setRelatedDrawer("")}
        title="Professional Profile Details"
        width="min(980px, 100vw)"
        className="olreadia-flow-drawer care-staff-related-drawer"
        destroyOnClose
      >
        {professionalForm.professionalProfileId ? (
          <ProfessionalProfileDetails
            profileIdOverride={professionalForm.professionalProfileId}
            embedded
            hidePromotions
            readOnly
          />
        ) : null}
      </Drawer>

      {documentPreview ? (
        <div className="care-profile-document-dialog" role="dialog" aria-modal="true" aria-label={documentPreview.title || "Document preview"}>
          <button className="care-profile-document-dialog__backdrop" type="button" aria-label="Close document preview" onClick={() => setDocumentPreview(null)} />
          <div className="care-profile-document-dialog__panel">
            <div className="care-profile-document-dialog__head">
              <div>
                <strong>{documentPreview.title || "Document Preview"}</strong>
                <small>
                  {documentPreview.label} · {documentPreview.name}
                  {documentPreview.galleryPages?.length ? ` · ${documentPreview.index + 1} of ${documentPreview.galleryPages.length}` : ""}
                </small>
              </div>
              <div className="care-profile-document-dialog__actions">
                <button className="care-profile-document-tool-btn" type="button" aria-label="Rotate document left" title="Rotate left" onClick={() => rotateDocumentPreview(-90)}>
                  <i className="ti ti-rotate-2" />
                </button>
                <button className="care-profile-document-tool-btn" type="button" aria-label="Rotate document right" title="Rotate right" onClick={() => rotateDocumentPreview(90)}>
                  <i className="ti ti-rotate-clockwise-2" />
                </button>
                <button className="care-profile-document-tool-btn" type="button" aria-label="Reset document rotation" title="Reset rotation" onClick={() => setDocumentPreviewRotation(0)}>
                  <i className="ti ti-refresh" />
                </button>
                <button className="care-profile-document-tool-btn close" type="button" aria-label="Close document preview" title="Close" onClick={() => setDocumentPreview(null)}>
                  <i className="ti ti-x" />
                </button>
              </div>
            </div>
            <div className="care-profile-document-dialog__body">
              <button
                className="care-profile-document-nav-btn previous"
                type="button"
                onClick={() => navigateDocumentPreview(-1)}
                disabled={(documentPreview.galleryPages?.length || 0) < 2}
              >
                <i className="ti ti-chevron-left" /> Back
              </button>
              <div className={`care-profile-document-dialog__frame${documentPreviewRotation % 180 !== 0 ? " rotated" : ""}`}>
                {workAuthorizationPageIsImage(documentPreview) ? (
                  <img src={documentPreview.signedUrl} alt="" style={{ transform: `rotate(${documentPreviewRotation}deg)` }} />
                ) : (
                  <iframe src={documentPreview.signedUrl} title={documentPreview.name || "Document preview"} style={{ transform: `rotate(${documentPreviewRotation}deg)` }} />
                )}
              </div>
              <button
                className="care-profile-document-nav-btn next"
                type="button"
                onClick={() => navigateDocumentPreview(1)}
                disabled={(documentPreview.galleryPages?.length || 0) < 2}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </CareShell>
  );
}
